import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ButtonLink, Container } from '~/components/ui';
import { cn } from '~/lib/cn';
import { useContent, useLocalizedPath } from '~/lib/useLanguage';
import { useSiteAvailability } from '~/lib/useSiteAvailability';
import { Logo } from './Logo';
import { MegaMenu } from './MegaMenu';
import { MobileNav } from './MobileNav';

// Nav pill: Figma H5 (17px SemiBold, ls -0.034px, lh 1.16), 16/12 padding.
// Radius is applied per-use so the right-cluster items can be fully rounded.
const pillClass =
  'rounded-[20px] px-4 pt-[12px] pb-[13px] text-[17px] leading-[1.16] font-semibold tracking-[-0.034px] text-grey-900 transition-colors';
// Same pill, fully rounded (matches the "Get the app" button).
const pillClassRounded = pillClass.replace('rounded-[20px]', 'rounded-pill');

// Scroll past this many px before the transparent header turns solid white.
// Small enough that any real scroll flips it, large enough to ignore rubber-band
// jitter at the very top.
const SCROLL_THRESHOLD = 12;

/**
 * Tracks whether the window has scrolled past `threshold`. SSR-safe: starts at
 * `false` (top of page) and only touches `window` inside an effect. When
 * `enabled` is false no listener is attached (always returns false — callers
 * treat the header as solid in that case).
 *
 * The initial read + every scroll update happen inside a rAF callback (never
 * synchronously in the effect body), which keeps it off the
 * `react-hooks/set-state-in-effect` path and coalesces scroll events to one
 * state update per frame.
 */
function useScrolledPast(enabled: boolean, threshold: number) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    let raf = 0;
    const measure = () => {
      raf = 0;
      setScrolled(window.scrollY > threshold);
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(measure);
    };

    // Seed the initial value (handles a reload while already scrolled) via rAF
    // so the state update stays out of the effect body.
    raf = requestAnimationFrame(measure);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [enabled, threshold]);

  return enabled && scrolled;
}

/**
 * Sticky site header + hover-driven mega menu.
 *
 * Reusable across every page (rendered once in PageShell). Hovering the
 * "Personal" pill slides the mega menu down from behind the bar and blurs
 * the page behind it; the header stays above everything (z-50).
 *
 * By default the header is always solid white. Pass `transparentUntilScroll`
 * (opt-in per page, e.g. the send-money-home grey hero) to make it transparent
 * at the top of the page and fade to solid white once the user scrolls past a
 * small threshold. Opening either menu forces solid white immediately so the
 * panel content stays readable, regardless of scroll position.
 */
export function Header({ transparentUntilScroll = false }: { transparentUntilScroll?: boolean }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { stablecoin: stablecoinAvailable } = useSiteAvailability();
  const { navigation, ui } = useContent();
  const { getAppCta, headerNav, learnMenu, mobileNav } = navigation;
  const localizedPathFor = useLocalizedPath();

  const scrolled = useScrolledPast(transparentUntilScroll, SCROLL_THRESHOLD);
  // The header is solid white unless it opted into transparency AND is at the
  // top AND has no menu open. `menuOpen || mobileOpen` wins over the transparent
  // state so the mega-menu / drawer content never sits on a transparent bar.
  // `transition-colors` (below) fades this white in/out over the same window as
  // the menu open/close animation, so nothing flashes on transparent mid-anim.
  const solid = !transparentUntilScroll || scrolled || menuOpen || mobileOpen;

  // Escape closes the mega menu.
  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      <header
        className={cn(
          'sticky top-0 z-50 transition-colors duration-300 ease-nav',
          solid ? 'bg-white' : 'bg-transparent',
        )}>
        {/* Leaving the header/menu region closes the mega menu. */}
        <div onMouseLeave={closeMenu}>
          <Container className="relative flex h-16 items-center justify-between">
            <Link
              to={localizedPathFor('/')}
              aria-label={ui.header.homeLabel}
              className="shrink-0"
              onMouseEnter={closeMenu}>
              <Logo />
            </Link>

            {/* Center pills — absolutely centered so they stay centered on the page */}
            <nav
              aria-label={ui.header.mainNavLabel}
              className="absolute top-1/2 left-1/2 hidden -translate-x-1/2 -translate-y-1/2 items-center gap-2 lg:flex">
              {headerNav.map((item) =>
                item.hasMenu ? (
                  // Trigger doubles as a link: hover/focus opens the mega menu,
                  // a click navigates to the item's page (and closes the menu).
                  <Link
                    key={item.label}
                    to={localizedPathFor(item.href!)}
                    onMouseEnter={() => setMenuOpen(true)}
                    onFocus={() => setMenuOpen(true)}
                    onClick={closeMenu}
                    aria-expanded={menuOpen}
                    className={cn(
                      pillClass,
                      menuOpen ? 'bg-black/[0.047]' : 'hover:bg-black/[0.047]',
                    )}>
                    {item.label}
                  </Link>
                ) : (
                  <Link
                    key={item.label}
                    to={localizedPathFor(item.href!)}
                    onMouseEnter={closeMenu}
                    className={cn(pillClass, 'hover:bg-black/[0.047]')}>
                    {item.label}
                  </Link>
                ),
              )}
            </nav>

            {/* Right cluster — Learn + Get the app */}
            <div className="flex items-center gap-2" onMouseEnter={closeMenu}>
              <div className="group relative hidden lg:block">
                <a
                  href={localizedPathFor('/help')}
                  aria-haspopup="true"
                  className={cn(
                    pillClassRounded,
                    'inline-flex group-focus-within:bg-black/[0.047] group-hover:bg-black/[0.047]',
                  )}>
                  {ui.header.learn}
                </a>
                <div className="invisible absolute top-full right-0 pt-2 opacity-0 transition-opacity duration-200 group-focus-within:visible group-focus-within:opacity-100 group-hover:visible group-hover:opacity-100">
                  <ul className="flex min-w-[180px] flex-col rounded-[20px] bg-white p-2 shadow-[0_8px_32px_rgba(0,0,0,0.12)]">
                    {learnMenu.map((link) => (
                      <li key={link.href}>
                        <a
                          href={link.external ? link.href : localizedPathFor(link.href)}
                          {...(link.external && { target: '_blank', rel: 'noopener noreferrer' })}
                          className={cn(
                            pillClass,
                            'block whitespace-nowrap hover:bg-black/[0.047]',
                          )}>
                          {link.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              {/* Canonical href on purpose: ButtonLink localizes it itself, and it
                  matches the raw path against GET_THE_APP_PATH to pick the mobile
                  store redirect / desktop QR modal. Pre-prefixing here would break
                  that branch the day an Arabic /get-the-app route lands. */}
              <ButtonLink href={getAppCta.href} className="font-semibold max-lg:!hidden">
                {getAppCta.label}
              </ButtonLink>
            </div>
          </Container>

          <MegaMenu
            open={menuOpen}
            onNavigate={closeMenu}
            stablecoinAvailable={stablecoinAvailable}
          />
        </div>
      </header>

      {/* Page-blurring backdrop; sits below the header (z-50) but above content. */}
      <div
        aria-hidden
        className={cn(
          'fixed inset-0 z-40 bg-blur-backdrop/60 backdrop-blur-sm transition-opacity duration-300',
          menuOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      />

      {/* Mobile right controls — Get the app sits left of the hamburger.
          Both float above the drawer (z-70 > z-60). CTA hides while the
          menu is open so only the close icon remains over the panel. */}
      <div className="fixed top-3 right-5 z-[70] flex translate-x-[4px] items-center gap-2 lg:hidden">
        {!mobileOpen && (
          <ButtonLink
            href={getAppCta.href}
            className="!min-w-0 !px-3 !pt-[11px] !pb-[12px] !text-[14px] !leading-[1.2] font-semibold">
            {getAppCta.label}
          </ButtonLink>
        )}
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label={mobileOpen ? mobileNav.closeMenuLabel : mobileNav.openMenuLabel}
          aria-expanded={mobileOpen}
          className="flex size-10 items-center justify-center text-ink">
          <MenuToggleIcon open={mobileOpen} />
        </button>
      </div>

      <MobileNav
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        stablecoinAvailable={stablecoinAvailable}
      />
    </>
  );
}

/** Two-bar hamburger that morphs into an X when `open`. Square line caps. */
function MenuToggleIcon({ open }: { open: boolean }) {
  const bar = 'absolute left-0 h-[2px] w-full bg-current transition-all duration-300 ease-nav';
  return (
    <span className="relative block h-4 w-5">
      <span className={cn(bar, open ? 'top-1/2 -translate-y-1/2 rotate-45' : 'top-[3px]')} />
      <span className={cn(bar, open ? 'top-1/2 -translate-y-1/2 -rotate-45' : 'top-[11px]')} />
    </span>
  );
}
